import { mapValues, isObjectLike, isDate } from 'lodash'
import { ALLOW_REDIRECT_URLS, FINLAND_ID } from 'lib/constants'
import { isProductionHost } from 'lib/env'
import { logExceptionDebug } from 'lib/errors'
import localStorage from 'lib/localStorage'
import { ALMA_AUTH0_CLIENT_ID } from 'lib/alma/alma'

export const walkThroughtObjectValuesDeep = (object, callback) => {
    const values = { ...object }
    return mapValues(values, (value) => {
        if (isDate(value)) return callback(value)
        if (isObjectLike(value)) return walkThroughtObjectValuesDeep(value, callback)
        return callback(value)
    })
}

export const sleep = (ms) => {
    return new Promise(resolve => setTimeout(resolve, ms))
}

export function getCountryId (countryNameInEnglish, countryOptions) {
    // Default to hardcoded Finland country id matching the one in Nettix Api, Nettiauto database
    const defaultId = 73
    if (!countryNameInEnglish || !countryOptions) return defaultId

    const country = countryOptions.filter(currentCountry => {
        return currentCountry.en === countryNameInEnglish
    })[0]

    return country.id
}

export function getFinlandCountryId (countryOptions) {
    return getCountryId('Finland', countryOptions)
}

export function getCountrySelectOptions (options, lang) {
    if (options != null) {
        const countries = []

        options.forEach((country) => {
            countries.push({ value: country.id, name: country[lang] })
        })

        countries.sort((a, b) => {
            return (a.name < b.name) ? -1 : (a.name > b.name) ? 1 : 0
        })

        const finlandOption = countries?.find(option => parseInt(option.value) === FINLAND_ID)
        const filteredOptions = countries?.filter(option => parseInt(option.value) !== FINLAND_ID)

        return [finlandOption, ...filteredOptions]
    }

    return false
}

export function isTouchSupport () {
    const touchSupport = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0) || (navigator.msMaxTouchPoints > 0)

    return touchSupport
}

export function getOffsetTop (selector) {
    const rect = document.querySelector(selector).getBoundingClientRect()
    const scrollTop = window.pageYOffset || document.querySelector(selector).scrollTop

    return rect.top + scrollTop
}

export function scrollTop (offset = 0) {
    try {
        window.scrollTo({
            top: offset,
            behavior: 'smooth'
        })
    } catch (error) {
        if (error instanceof TypeError) {
            window.scrollTo(0, offset)
        } else {
            throw error
        }
    }
}

export function scrollToSelector (selector, offset = -100) {
    const target = document.querySelector(selector)
    if (target) {
        scrollToElement(target, offset)
    }
}

export function scrollToElement (element, offset = -100) {
    const rect = element.getBoundingClientRect()
    const offsetTop = window.pageYOffset || element.scrollTop
    scrollTop(rect.top + offsetTop + offset)
}

export function friendlyFormatIBAN (iban) {
    return (iban ? iban.toUpperCase() : '').replace(/[^\dA-Z]/g, '').replace(/(.{4})/g, '$1 ').trim()
}

export function friendlyFormatRefNumber (refnumber) {
    return refnumber.split(/(?=(?:.....)*$)/).join(' ')
}

/**
 * Check if we have element in dom that is trying to prevent navigation. eg. form that is in progress and not saved.
 */
export function getPreventNavigationElement () {
    const element = document.querySelector('[data-prevent-navigation]')
    if (!element) return false

    const message = element.getAttribute('data-prevent-navigation')
    if (message && message !== 'false') {
        return {
            message: message,
            element: element,
        }
    }

    return false
}

export function isAllowedRedirectUrl (url) {
    const domain = url && ALLOW_REDIRECT_URLS.domains.find(domain => (
        url.toLowerCase().indexOf(`.${domain}`) !== -1 ||
        url.toLowerCase().indexOf(`://${domain}`) !== -1
    ))

    if (!domain) {
        const { hostname } = new URL(url)

        return (
            ALLOW_REDIRECT_URLS.expr.some((value) => {
                return (new RegExp(value)).test(hostname)
            })
        )
    }

    return true
}

export function isBase64Encoded (string) {
    const re = /^([0-9a-zA-Z+/]{4})*(([0-9a-zA-Z+/]{2}==)|([0-9a-zA-Z+/]{3}=))?$/

    return re.test(string)
}

export function isVerticalCheck (query, existingVehicle) {
    if (query?.vertical && existingVehicle && existingVehicle?.vertical) {
        return existingVehicle.vertical === query.vertical
    }

    return true
}

export function encodeQueryParam (url) {
    const urlObject = new URL(url)

    const successUrl = encodeURIComponent(urlObject.searchParams.get('successUrl') || '')
    const failureUrl = encodeURIComponent(urlObject.searchParams.get('failureUrl') || '')
    const offerId = encodeURIComponent(urlObject.searchParams.get('offerId') || '')
    const encodedOfferId = encodeURIComponent(`&offerId=${offerId}`).replace('&', '%26')

    urlObject.searchParams.set('successUrl', successUrl)
    urlObject.searchParams.set('failureUrl', failureUrl)
    urlObject.searchParams.set('offerId', encodedOfferId)

    const updatedUrl = offerId
        ? `https://${urlObject.hostname}${urlObject.pathname}?successUrl=${successUrl}${encodedOfferId}&failureUrl=${failureUrl}`
        : `https://${urlObject.hostname}${urlObject.pathname}?successUrl=${successUrl}&failureUrl=${failureUrl}`

    return updatedUrl.toString()
}

/**
 * If language is english, add "/en" to the url, otherwise remove it.
 **/
export function updatePathWithOrWithoutLangParam (lang, pathname) {
    const isEnglish = lang === 'en'
    const hasEnPath = pathname.includes('/en')

    if (isEnglish && !hasEnPath) {
        return pathname === '/' ? '/en' : `/en${pathname}`
    }

    if (!isEnglish && hasEnPath) {
        return pathname.replace('/en', '')
    }

    return pathname
}

export function urlWithLangParam (url, lang) {
    if (url === null || !url) {
        return null
    }

    const langPath = lang === 'en' ? '/en' : ''
    const isUrlObject = typeof url === 'object' && url.href
    const urlString = isUrlObject ? url.href : url

    // For debug log
    if (typeof url === 'object') {
        logExceptionDebug(`urlString is an object: ${urlString}`)
    }

    if (!isProductionHost()) {
        if (typeof urlString === 'object') {
            return url
        }
    }

    const urlObj = new URL(urlString)

    /**
     * If language is english, add "/en" to the url, otherwise remove it.
     **/
    const updatedPathName = (urlObject) => {
        let updatedPath = urlObject.pathname
        const isIncludeEn = urlObject.pathname.includes('/en')
        const isRefreshUrl = urlObject.pathname.includes('/sso/refresh') || urlObject.pathname.includes('/en/sso/refresh')

        /**
         * If /sso/refresh exists in the pathname, we don't add /en to the URL. In fact, if it exists in the URL, we remove it because the responsive vertical does not have support.
         **/
        if (isRefreshUrl) {
            isIncludeEn && (updatedPath = urlObject.pathname.replace('/en', ''))
        } else {
            if (lang === 'en') {
                !isIncludeEn && (updatedPath = urlObject.pathname !== '/' ? `${langPath}${urlObject.pathname}` : `${langPath}`)
            } else {
                isIncludeEn && (updatedPath = urlObject.pathname.replace('/en', ''))
            }
        }

        return updatedPath
    }

    if (urlObj?.search.includes('successUrl') && urlObj?.search.includes('failureUrl')) {
        const successUrl = urlObj.searchParams.get('successUrl') || ''
        const failureUrl = urlObj.searchParams.get('failureUrl') || ''

        const updateUrlLang = (urlStr) => {
            const urlLangObj = new URL(urlStr)
            urlLangObj.pathname = updatedPathName(urlLangObj)
            return urlLangObj.toString()
        }

        urlObj.searchParams.set('successUrl', updateUrlLang(successUrl))
        urlObj.searchParams.set('failureUrl', updateUrlLang(failureUrl))
        urlObj.search = urlObj.searchParams.toString()
    }

    urlObj.pathname = updatedPathName(urlObj)

    if (isUrlObject) {
        url.href = urlObj.toString()
    }

    return isUrlObject ? url : urlObj.toString()
}

export function urlWithoutLoginRedirectParam (url = null) {
    if (url === null || !url) {
        url = new URL(window.location.href)
    }
    url.searchParams.delete('loginRedirect')

    return url.pathname + url.search + url.hash
}

export function clearAuth0UserSession () {
    console.log('Clearing Auth0 user session')

    localStorage.removeItem(`@@auth0spajs@@::${ALMA_AUTH0_CLIENT_ID}::@@user@@`)
    localStorage.removeItem(`@@auth0spajs@@::${ALMA_AUTH0_CLIENT_ID}::https://tunnus-dev.almamedia.net::openid profile email`)
}
